import React, { useMemo, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from '@material-ui/core';

export default function NameTableDialog({
  open,
  onClose,
  address,
  label,
  onLabelChange,
  iconUrl,
  onIconUrlChange,
  onUploadFileChange,
  error,
  saving,
  onSave,
  canSave = true,
}) {
  try {
    if (open) {
      console.log('[nametable] NameTableDialog open');
    }
  } catch (e) {
  }

  const paperStyle = {
    background: 'linear-gradient(180deg, rgba(10,14,33,0.98) 0%, rgba(6,8,20,0.98) 100%)',
    color: '#e7e9ff',
    border: '1px solid rgba(87,116,255,0.65)',
    boxShadow: '0 18px 60px rgba(0,0,0,0.65)',
  };

  const titleStyle = {
    fontWeight: 800,
    letterSpacing: 0.2,
    paddingBottom: 6,
  };

  const labelColor = { color: 'rgba(231,233,255,0.82)' };

  const fieldCommon = {
    variant: 'outlined',
    size: 'small',
    fullWidth: true,
    InputLabelProps: { style: labelColor },
    InputProps: {
      style: {
        color: '#e7e9ff',
        background: 'rgba(255,255,255,0.04)',
      },
    },
  };

  const helperStyle = {
    marginTop: 8,
    fontSize: 12,
    color: 'rgba(199,205,255,0.82)',
    lineHeight: 1.35,
  };

  const errorStyle = {
    marginTop: 10,
    fontSize: 12,
    color: 'rgba(255,125,125,0.95)',
  };

  const primaryBtnStyle = {
    background: 'rgba(77,121,255,0.9)',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
  };

  const secondaryBtnStyle = {
    color: 'rgba(231,233,255,0.9)',
    textTransform: 'none',
    fontWeight: 700,
  };

  const infoBoxStyle = {
    padding: '10px 12px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255, 229, 92, 0.25)',
  };

  const [selectedFileName, setSelectedFileName] = useState('');
  const fileInputRef = useRef(null);

  const acceptList = useMemo(
    () => [
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      '.png',
      '.jpg',
      '.jpeg',
      '.gif',
      '.webp',
      '.svg',
    ].join(','),
    []
  );

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: paperStyle }}
    >
      <DialogTitle style={titleStyle}>Edit Nametable</DialogTitle>
      <DialogContent style={{ paddingTop: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!canSave && (
            <div style={infoBoxStyle}>
              <Typography variant="body2" style={{ color: 'rgba(231,233,255,0.92)', fontSize: 13, fontWeight: 700 }}>
                Saving requires an account
              </Typography>
              <Typography variant="body2" style={{ color: 'rgba(199,205,255,0.86)', fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>
                This feature stores personal names and icons. Please log in (free) or subscribe to Premium to save changes.
              </Typography>
            </div>
          )}
          <TextField
            {...fieldCommon}
            label="Address"
            value={address || ''}
            disabled
            inputProps={{
              style: {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 12,
              },
            }}
          />

          <TextField
            {...fieldCommon}
            label="Personal label"
            value={label || ''}
            onChange={(e) => onLabelChange(e.target.value)}
          />

          <TextField
            {...fieldCommon}
            label="Icon URL"
            value={iconUrl || ''}
            onChange={(e) => onIconUrlChange(e.target.value)}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={helperStyle}>
              Paste a URL (https://...) or a local path (/icons/...). Or upload an image file.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptList}
                style={{ display: 'none' }}
                onClick={(e) => {
                  try {
                    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                  } catch (err) {
                  }
                }}
                onChange={(e) => {
                  try {
                    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                  } catch (err) {
                  }
                  const file = (e && e.target && e.target.files && e.target.files[0]) ? e.target.files[0] : null;
                  try {
                    setSelectedFileName(file ? String(file.name || '') : '');
                  } catch (err) {
                  }
                  onUploadFileChange(file);
                }}
              />
              <Button
                variant="outlined"
                disabled={!!saving}
                style={{
                  ...secondaryBtnStyle,
                  borderColor: 'rgba(87,116,255,0.45)',
                  background: 'rgba(255,255,255,0.03)',
                }}
                onClick={(e) => {
                  try {
                    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
                    if (fileInputRef.current) fileInputRef.current.click();
                  } catch (err) {
                  }
                }}
              >
                Choose image…
              </Button>
              {selectedFileName ? (
                <div style={{ fontSize: 12, color: 'rgba(231,233,255,0.85)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedFileName}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'rgba(199,205,255,0.72)' }}>
                  PNG/JPG/GIF/WEBP/SVG
                </div>
              )}
            </div>
            {error ? (
              <div style={errorStyle}>{error}</div>
            ) : null}
          </div>
        </div>
      </DialogContent>
      <DialogActions style={{ padding: '8px 16px 14px' }}>
        <Button onClick={onClose} disabled={!!saving} style={secondaryBtnStyle}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={!!saving} variant="contained" style={primaryBtnStyle}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
